import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCourseStore } from '../../../context/CourseStoreContext';
import { useAuth } from '../../../context/useAuth';

export default function AdminDashboardPage() {
  const { courses } = useCourseStore();
  const { user } = useAuth();

  const stats = useMemo(() => {
    const totalLessons = courses.reduce((sum, c) => sum + c.lessons.length, 0);
    const totalStudents = courses.reduce((sum, c) => sum + c.students, 0);
    const avgRating =
      courses.length > 0
        ? (courses.reduce((sum, c) => sum + c.rating, 0) / courses.length).toFixed(1)
        : '0.0';
    const totalRevenue = courses.reduce((sum, c) => sum + c.price * c.students, 0);
    return { totalCourses: courses.length, totalLessons, totalStudents, avgRating, totalRevenue };
  }, [courses]);

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const statCards = [
    {
      label: 'Total Courses',
      value: stats.totalCourses,
      icon: 'ri-book-3-line',
      change: '+2 this month',
      positive: true,
      gradient: 'linear-gradient(135deg, #0d9488, #0f766e)',
      bg: 'rgba(20,184,166,0.08)',
    },
    {
      label: 'Total Students',
      value: stats.totalStudents.toLocaleString(),
      icon: 'ri-group-2-line',
      change: '+340 this week',
      positive: true,
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      bg: 'rgba(245,158,11,0.08)',
    },
    {
      label: 'Total Lessons',
      value: stats.totalLessons,
      icon: 'ri-play-circle-2-line',
      change: `Across ${stats.totalCourses} courses`,
      positive: true,
      gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      bg: 'rgba(99,102,241,0.08)',
    },
    {
      label: 'Avg. Rating',
      value: stats.avgRating,
      icon: 'ri-star-smile-line',
      change: 'Platform average',
      positive: true,
      gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
      bg: 'rgba(239,68,68,0.08)',
    },
  ];

  const topCourses = [...courses]
    .sort((a, b) => b.students - a.students)
    .slice(0, 5);

  const recentCourses = [...courses]
    .sort((a, b) => b.id.localeCompare(a.id))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div
        className="rounded-2xl p-6 flex items-center justify-between overflow-hidden relative"
        style={{
          background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 60%, #0d9488 100%)',
        }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 80% 50%, #2dd4bf 0%, transparent 60%)',
          }}
        />
        <div className="relative">
          <p className="text-teal-300 text-sm font-medium mb-1">{greeting},</p>
          <h2 className="text-2xl font-bold text-white mb-1">{user?.name ?? 'Admin'} 👋</h2>
          <p className="text-slate-400 text-sm">
            Here&apos;s what&apos;s happening with your platform today.
          </p>
        </div>
        <div className="relative flex items-center gap-3">
          <Link
            to="/admin/courses/new"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer transition-all hover:scale-105"
            style={{ background: 'linear-gradient(90deg, #0d9488, #0f766e)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-circle-line" />
            </div>
            Create Course
          </Link>
          <Link
            to="/admin/courses"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105"
            style={{ background: 'rgba(255,255,255,0.1)', color: '#e2e8f0' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-list-check-3" />
            </div>
            All Courses
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-5">
        {statCards.map(card => (
          <div
            key={card.label}
            className="bg-white rounded-2xl p-5 flex flex-col gap-3"
            style={{ border: '1px solid #f1f5f9' }}
          >
            <div className="flex items-start justify-between">
              <div
                className="w-11 h-11 flex items-center justify-center rounded-xl text-white text-lg"
                style={{ background: card.gradient }}
              >
                <i className={card.icon} />
              </div>
              <span
                className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: card.bg, color: card.gradient.includes('0d9488') ? '#0d9488' : card.gradient.includes('f59e0b') ? '#d97706' : card.gradient.includes('6366f1') ? '#4f46e5' : '#ef4444' }}
              >
                {card.positive ? '↑' : '↓'} Active
              </span>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-slate-900 leading-none">{card.value}</p>
              <p className="text-sm text-slate-500 mt-1">{card.label}</p>
            </div>
            <p className="text-xs text-slate-400 font-medium">{card.change}</p>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top Courses by Students */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid #f1f5f9' }}
        >
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ borderBottom: '1px solid #f1f5f9' }}
          >
            <div>
              <h3 className="font-bold text-slate-900 text-base">Top Courses</h3>
              <p className="text-xs text-slate-400 mt-0.5">By number of enrolled students</p>
            </div>
            <Link
              to="/admin/courses"
              className="text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
            >
              View all &rarr;
            </Link>
          </div>
          <div className="p-4 space-y-3">
            {topCourses.map((course, index) => {
              const max = topCourses[0]?.students || 1;
              const pct = Math.round((course.students / max) * 100);
              return (
                <div key={course.id} className="flex items-center gap-3">
                  <span
                    className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-extrabold flex-shrink-0"
                    style={{
                      background: index === 0 ? 'linear-gradient(135deg,#f59e0b,#d97706)' : index === 1 ? 'linear-gradient(135deg,#94a3b8,#64748b)' : 'linear-gradient(135deg,#d97706,#b45309)',
                      color: '#fff',
                    }}
                  >
                    {index + 1}
                  </span>
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-10 h-8 rounded-lg object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{course.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div
                        className="flex-1 h-1.5 rounded-full overflow-hidden"
                        style={{ background: '#f1f5f9' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: 'linear-gradient(90deg,#0d9488,#2dd4bf)',
                          }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {course.students.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recently Added */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid #f1f5f9' }}
        >
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ borderBottom: '1px solid #f1f5f9' }}
          >
            <div>
              <h3 className="font-bold text-slate-900 text-base">Recent Courses</h3>
              <p className="text-xs text-slate-400 mt-0.5">Latest courses on the platform</p>
            </div>
            <Link
              to="/admin/courses/new"
              className="flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
            >
              <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line" /></div>
              Add New
            </Link>
          </div>
          <div className="divide-y divide-slate-50">
            {recentCourses.map(course => (
              <div key={course.id} className="flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50/70 transition-colors">
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-12 h-9 rounded-xl object-cover object-top flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{course.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{course.instructor}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      course.level === 'Beginner'
                        ? 'bg-emerald-50 text-emerald-700'
                        : course.level === 'Intermediate'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-rose-50 text-rose-700'
                    }`}
                  >
                    {course.level}
                  </span>
                  <Link
                    to={`/admin/courses/${course.id}/edit`}
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-teal-600 hover:bg-teal-50 cursor-pointer transition-colors"
                  >
                    <i className="ri-edit-2-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div
        className="bg-white rounded-2xl p-6"
        style={{ border: '1px solid #f1f5f9' }}
      >
        <h3 className="font-bold text-slate-900 text-base mb-4">Quick Actions</h3>
        <div className="grid grid-cols-4 gap-4">
          {[
            { to: '/admin/courses/new', icon: 'ri-add-circle-2-line', label: 'Create Course', desc: 'Add a new course to the platform', gradient: 'linear-gradient(135deg,#0d9488,#0f766e)' },
            { to: '/admin/courses', icon: 'ri-book-3-line', label: 'Manage Courses', desc: 'Edit, preview or delete courses', gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)' },
            { to: '/courses', icon: 'ri-eye-line', label: 'View Platform', desc: 'Browse the live course catalog', gradient: 'linear-gradient(135deg,#f59e0b,#d97706)' },
            { to: '/', icon: 'ri-home-4-line', label: 'Homepage', desc: 'Go back to the main site', gradient: 'linear-gradient(135deg,#ef4444,#dc2626)' },
          ].map(action => (
            <Link
              key={action.to}
              to={action.to}
              className="flex flex-col items-start gap-3 p-4 rounded-2xl cursor-pointer transition-all hover:-translate-y-0.5 group"
              style={{ background: '#f8fafc', border: '1px solid #f1f5f9' }}
            >
              <div
                className="w-11 h-11 flex items-center justify-center rounded-xl text-white text-xl"
                style={{ background: action.gradient }}
              >
                <i className={action.icon} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800 group-hover:text-teal-700 transition-colors">{action.label}</p>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{action.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
